import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { SearchKeyword, VisitorsData } from '@/types/masterLicenseSettings';
import { Plus, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const SEOReportsTab: React.FC = () => {
  // Sample data - in a real app, this would come from an API
  const [keywords, setKeywords] = useState<SearchKeyword[]>([
    { id: 1, keyword: "referral software", position: 3, volume: 2400, difficulty: 'Medium', change: 2 },
    { id: 2, keyword: "referral marketing platform", position: 5, volume: 1800, difficulty: 'Medium', change: -1 },
    { id: 3, keyword: "best referral program", position: 7, volume: 3200, difficulty: 'High', change: 0 },
    { id: 4, keyword: "referral tracking software", position: 4, volume: 1500, difficulty: 'Medium', change: 4 },
    { id: 5, keyword: "customer referral program", position: 2, volume: 2200, difficulty: 'Low', change: -2 },
    { id: 6, keyword: "referral system", position: 8, volume: 4100, difficulty: 'High', change: 1 },
    { id: 7, keyword: "referral link generator", position: 9, volume: 950, difficulty: 'Low', change: 0 },
    { id: 8, keyword: "affiliate tracking software", position: 12, volume: 3600, difficulty: 'High', change: -3 },
  ]);

  const [visitorData, setVisitorData] = useState<VisitorsData[]>([
    { date: "Apr 20", visitors: 342, newUsers: 205, bounceRate: 45.2 },
    { date: "Apr 21", visitors: 385, newUsers: 240, bounceRate: 42.7 },
    { date: "Apr 22", visitors: 420, newUsers: 270, bounceRate: 40.1 },
    { date: "Apr 23", visitors: 375, newUsers: 218, bounceRate: 43.5 },
    { date: "Apr 24", visitors: 398, newUsers: 245, bounceRate: 41.8 },
    { date: "Apr 25", visitors: 440, newUsers: 285, bounceRate: 38.2 },
    { date: "Apr 26", visitors: 480, newUsers: 310, bounceRate: 36.5 }
  ]);

  const visitorSourceData = [
    { name: 'Organic Search', value: 55 },
    { name: 'Direct', value: 20 },
    { name: 'Social Media', value: 15 },
    { name: 'Referral', value: 7 },
    { name: 'Email', value: 3 }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const getChangeIcon = (change: number) => {
    if (change > 0) return <ArrowUp className="text-green-600 h-4 w-4" />;
    if (change < 0) return <ArrowDown className="text-red-600 h-4 w-4" />;
    return <Minus className="text-gray-500 h-4 w-4" />;
  };

  const getChangeClass = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const getDifficultyClass = (difficulty: string) => {
    switch(difficulty) {
      case 'Low':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'High':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Search Rankings</CardTitle>
              <CardDescription>Track your website's position for important keywords</CardDescription>
            </div>
            <Button size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Keyword
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <th className="h-10 px-4 text-left font-medium">Keyword</th>
                  <th className="h-10 px-4 text-center font-medium">Position</th>
                  <th className="h-10 px-4 text-center font-medium">Change</th>
                  <th className="h-10 px-4 text-center font-medium">Search Volume</th>
                  <th className="h-10 px-4 text-center font-medium">Difficulty</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((keyword) => (
                  <tr key={keyword.id} className="border-b">
                    <td className="p-4 align-middle">{keyword.keyword}</td>
                    <td className="p-4 align-middle text-center">{keyword.position}</td>
                    <td className="p-4 align-middle text-center">
                      <div className="flex items-center justify-center">
                        {getChangeIcon(keyword.change)}
                        <span className={`ml-1 ${getChangeClass(keyword.change)}`}>
                          {Math.abs(keyword.change) || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 align-middle text-center">{keyword.volume.toLocaleString()}</td>
                    <td className="p-4 align-middle text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyClass(keyword.difficulty)}`}>
                        {keyword.difficulty}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Website Traffic Analytics</CardTitle>
          <CardDescription>Track visitor engagement and sources</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
              <TabsTrigger value="yearly">Yearly</TabsTrigger>
            </TabsList>
            
            <TabsContent value="daily" className="space-y-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visitorData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="visitors" stroke="#8884d8" activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="newUsers" stroke="#82ca9d" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Traffic Sources</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={visitorSourceData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {visitorSourceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-4">Bounce Rate Trend</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={visitorData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip formatter={(value) => `${value}%`} />
                        <Legend />
                        <Bar dataKey="bounceRate" fill="#ff8042" name="Bounce Rate" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Other tabs would have similar content with different timeframes */}
            <TabsContent value="weekly">
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                Weekly data visualization will appear here.
              </div>
            </TabsContent>
            
            <TabsContent value="monthly">
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                Monthly data visualization will appear here.
              </div>
            </TabsContent>
            
            <TabsContent value="quarterly">
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                Quarterly data visualization will appear here.
              </div>
            </TabsContent>
            
            <TabsContent value="yearly">
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                Yearly data visualization will appear here.
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOReportsTab;
